import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { TState } from '~/src/store';

import * as ST from './styled';

const grade_good = '/static/images/full-buy/grade_good.png';
const grade_good_webp = '/static/images/full-buy/grade_good.webp';
const grade_bad = '/static/images/full-buy/grade_bad.png';
const grade_bad_webp = '/static/images/full-buy/grade_bad.webp';

enum EFeedback {
    not_grade = 'not_grade',
    bad_grade = 'bad_grade',
    good_grade = 'good_grade',
}

const Feedback = (): JSX.Element => {
    const analytics = useAnalytics();

    const agreementNumber = useSelector((state: TState) => (state.fullBuy.agreement.agreementNumber));
    const [feedback, setFeedback] = useState(EFeedback.not_grade);
    const [isLoading] = useState(false);
    const [message, setMessage] = useState<string>('');
    const [isValid, setIsValid] = useState(false);
    const [startInput, setStartInput] = useState(false);
    const [isSentFeedback, setIsSentFeedback] = useState(false);

    const title = useMemo(() => {
        switch (feedback) {
            case EFeedback.not_grade:
                return 'Вам всё понравилось?';
            case EFeedback.bad_grade:
                return 'Будем исправляться';
            case EFeedback.good_grade:
                return 'Спасибо за вашу оценку!';
        }
    }, [feedback]);

    const sendGoodGrade = useCallback(() => {
        setFeedback(EFeedback.good_grade);
        analytics.feedback.positive(agreementNumber);
    }, []);

    const sendBadGrade = useCallback(() => {
        setFeedback(EFeedback.bad_grade);
        analytics.feedback.negative(agreementNumber);
    }, []);

    const sendBadGradeWithMessage = () => {
        setFeedback(EFeedback.bad_grade);
        setIsSentFeedback(true);
        analytics.feedback.negative(agreementNumber, message);
    };

    const onChangeMessage = (e: ChangeEvent<HTMLInputElement>) => {
        setStartInput(true);
        setIsValid(e.target.value !== '');
        setMessage(e.target.value);
    };

    return (
        <ST.FeedbackWrapper>
            <H3 $withIndent={false}>{title}</H3>
            <ST.FeedbackButtonsContainer>
                {feedback !== EFeedback.good_grade &&
                    <ST.FeedbackButton onClick={sendBadGrade} clickable={!isSentFeedback}>
                        <ST.FeedbackEmoticon>
                            <WebpImage urlWebp={grade_bad_webp}>
                                <img src={grade_bad} />
                            </WebpImage>
                        </ST.FeedbackEmoticon>
                        {feedback !== EFeedback.bad_grade && <ST.FeedbackButtonTitle>Можно было лучше</ST.FeedbackButtonTitle>}
                    </ST.FeedbackButton>
                }
                {feedback !== EFeedback.bad_grade &&
                    <ST.FeedbackButton onClick={sendGoodGrade} clickable={!isSentFeedback}>
                        <ST.FeedbackEmoticon>
                            <WebpImage urlWebp={grade_good_webp}>
                                <img src={grade_good} />
                            </WebpImage>
                        </ST.FeedbackEmoticon>
                        {feedback !== EFeedback.good_grade && <ST.FeedbackButtonTitle>Всё отлично</ST.FeedbackButtonTitle>}
                    </ST.FeedbackButton>
                }
            </ST.FeedbackButtonsContainer>
            {feedback === EFeedback.bad_grade &&
                (!isSentFeedback
                ? <div>
                    <ST.InputMessage
                        inputType={'text'}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeMessage(e)}
                        placeholder='Можете рассказать нам всё как есть'
                        name='message'
                        value={message}
                        isNotValid={startInput && !isValid}
                        errorMessage='Введите текст сообщения'
                        required
                    />
                    <ST.ButtonMessage
                        type={btnTypes.SECONDARY}
                        onClick={sendBadGradeWithMessage}
                        disabled={!isValid}
                    >
                        {'Отправить'}
                    </ST.ButtonMessage>
                </div>
                : <ST.FeedbackTitle>Спасибо за ваш отзыв!</ST.FeedbackTitle>)
            }
            {isLoading && <ST.StyledThrobber />}
        </ST.FeedbackWrapper>
    );
};

export default Feedback;
